import * as _ from 'lodash';
import { Component, DestroyRef, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { catchError, filter, first, share } from 'rxjs/operators';
import { AuthService } from 'app/router-wrapper/services/auth/auth.service';
import { ModalLoginService } from 'app/router-wrapper/services/modal-login/modal-login.service';
import { of } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { Store } from '@ngrx/store';
import { clearCampaignList } from 'app/campaign/features/campaign.actions';
import { loadOrgUser } from 'app/dashboard/features/dashboard.actions';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  destroyRef = inject(DestroyRef)
  public modalMainHeadingText = "Login";
  @Input() modalSubHeadingText = "Organization Log in to access PayBee Account";

  public loginAs = "Organization Login";

  public form: UntypedFormGroup = new UntypedFormGroup({
    email: new UntypedFormControl("", [Validators.required, Validators.email]),
    password: new UntypedFormControl("", [Validators.required]),
    organization: new UntypedFormControl(null, [])
  });
  public submitted = false;

  public organizations = [];

  public isLoginPage = true;
  public isSelectOrganizationPage = false;

  constructor(
    public authService: AuthService,
    public cookieService: CookieService,
    public modalLoginService: ModalLoginService,
    public store: Store
  ) {
    this.cookieService.delete('accessToken');
    this.cookieService.delete('adminAccessToken');

    const orgUser = this.authService.orgUser || {};
    if(orgUser.status === 4) {
      this.modalSubHeadingText = "Your account is blocked, please contact us if it was done in error";
    }

    if(orgUser.status === 10) {
      this.modalSubHeadingText = "Your account is closed, please contact us if it was done in error";
    }

    this.authService.reset();
  }

  ngOnInit(): void {
    this.form.get('organization').valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ name }) => {
      this.loginAs = `Login as ${name}`;
    });
  }

  ngOnDestroy(): void {
    this.modalLoginService.modalRef = null;
  }

  onSubmit() {
    const orgUtil = window['orgUtil'];
    this.submitted = true;

    const email = this.form.get('email');
    const password = this.form.get('password');

    if(this.form.invalid) {
      if(email.errors) {
        orgUtil.setInputError("signInEmail", this.getEMailErrorMessage());
        return false;
      }

      if(password.errors) {
        orgUtil.setInputError("signInPassword", this.getPasswordErrorMessage());
        return false;
      }

      return false;
    }

    const { email: emailField, password: passwordField } = this.form.value;
    const signin = this.authService.signin(emailField, passwordField).pipe(
      first(),
      share(),
    );

    signin.pipe(
      filter(response => _.has(response, 'error')),
      catchError((error) => {
        return of({ error });
      })
    ).subscribe(({ error }: any) => {
      switch(error.errorCode) {
        case "UserDoesNotExist":
            email.setErrors({ UserDoesNotExist: true });
            return false;
        case "InvalidPassword":
            password.setErrors({ InvalidPassword: true });
            return false;
        case "OrgAccountBlocked":
            email.setErrors({ OrgAccountBlocked: true });
            return false;
        case "OrgAccountClosed":
              email.setErrors({ OrgAccountClosed: true });
              return false;    
        case "NonOrgUser":
            email.setErrors({ NonOrgUser: true });
            return false;
        default:
      }
    });

    signin.pipe(
      filter((orgUser: any) => _.isNull(orgUser.orgs))
    ).subscribe((orgUser) => {
      this.authService.orgUser = orgUser;
      this.authService.resetOrgUserObservable();

      this.store.dispatch({
        type: loadOrgUser.type,
        payload:{reload:true}
      });
      this.modalLoginService.modalRef.close(true);
    });

    signin.pipe(
      filter((orgUser: any) => _.isArray(orgUser.orgs))
    ).subscribe((orgUser) => {
      this.authService.orgUser = orgUser;
      this.organizations = _.sortBy(orgUser.orgs, ['name']);

      this.isLoginPage = false;
      this.isSelectOrganizationPage = true;

      this.modalSubHeadingText = "Select an account to log in."
    });

    this.store.dispatch({
      type: clearCampaignList.type
    });
  }

  onSelectOrganization() {
    const { value: { id }} = this.form.get('organization');
    this.authService.loginAs(id).pipe(
      first(),
      catchError(error => {
        switch(error.errorCode) {
          case 'InvalidAccessToken':
            this.isLoginPage = true;
            this.isSelectOrganizationPage = false;
            
            this.modalSubHeadingText = "Invalid Access Token.";
            break;
          default:
            this.modalSubHeadingText = error.errorMessage || "Sorry! something has gone wrong, please try again.";
        }
        return error;
      })
    ).subscribe(() => {
      this.authService.resetOrgUserObservable();
      this.store.dispatch({
        type: loadOrgUser.type,
        payload:{reload:true}
      });
      this.modalLoginService.modalRef.close(true);
    });
  }

  onForgotPassword(): void {
    const orgUtil = window['orgUtil'];
   let email = this.form.get('email').value
   if(email){
      window['orgSignIn'].forgotPassword(email).then((val)=>{
        if(val){
          this.modalSubHeadingText = 'Email with reset password link has been sent if you don`t see it in your inbox please check your spam folder.';
          orgUtil.resetInputError("signInEmail");
        }
      }).catch((error) => {
        switch(error.errorCode) {
          case "InvalidUserPhoneOrEmail":
              orgUtil.setInputError("signInEmail", "No account associated with this email.")
              return
          default:
            window['$']("#loginError").show()
            window['$']("#loginError").text('Sorry! something has gone wrong, please try again.')
      }
      });
   }else{
      orgUtil.setInputError("signInEmail", this.getEMailErrorMessage());
   }

  }

  onSignUp(): void {
    window['orgSignIn'].redirectToSignUp()
  }

  getEMailErrorMessage() {
    const { form } = this;
    const control = form.get('email');
    if(control.errors) {
      const { errors } = control;
      if(errors.required) {
        return 'Field E-Mail is required';
      }
      if(errors.email) {
        return 'Invalid E-Mail format (example: example@paybee.io)';
      }
      if(errors.UserDoesNotExist) {
        return 'User doesn\'t exist';
      }
      if(errors.NonOrgUser) {
        return 'Organization User doesn\'t exist';
      }
      if(errors.OrgAccountBlocked) {
        return 'This account has been blocked, write to cotact@paybee.io if it was done in error.'
      }
      if(errors.OrgAccountClosed) {
        return 'This account has been closed, write to cotact@paybee.io if it was done in error.'
      }
    }
    return '';
  }

  getPasswordErrorMessage() {
    const { form } = this;
    const control = form.get('password');
    if(control.errors) {
      const { errors } = control;
      if(errors.required) {
        return 'Field Password is required';
      }
      if(errors.InvalidPassword) {
        return 'Incorrect password';
      }
    }
    return '';
  }
  

}
